/**
 * Flushes cookie preference from localStorage to the API after auth (PBI #1154).
 *
 * Business rule: stores Analytics/Marketing opt-in on the User row so Admin can
 * reconcile Google Ads Free signup / Subscribe gaps against consent. Best-effort —
 * never blocks auth or conversion flows.
 */

import apiClient from './apiClient';
import { CONSENT_STORAGE_KEY } from '../constants/consent';
import type { StoredConsent } from '../types/consent';
import { detectGpc } from '../utils/detectGpc';

/** Authenticated account endpoint for cookie preference snapshot. */
export const COOKIE_CONSENT_API_PATH = '/account/cookie-consent';

/**
 * Reads stored CMP preference from localStorage when a choice has been made.
 * Applies GPC so the server ledger matches Consent Mode signals Ads would see.
 *
 * @returns Analytics/marketing flags, or null when unset / invalid
 */
export function readStoredCookieConsentForFlush(): { analytics: boolean; marketing: boolean } | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredConsent;
    if (
      typeof parsed?.consent?.analytics !== 'boolean' ||
      typeof parsed?.consent?.marketing !== 'boolean'
    ) {
      return null;
    }

    return {
      analytics: parsed.consent.analytics,
      marketing: detectGpc() ? false : parsed.consent.marketing,
    };
  } catch {
    return null;
  }
}

/**
 * Best-effort POST of stored cookie preference. No-ops when consent is unset.
 * Swallows errors so callers (auth init, banner Accept, conversion) are never blocked.
 *
 * @param consent Optional explicit snapshot; defaults to localStorage read
 */
export async function flushCookieConsentToServer(consent?: {
  analytics: boolean;
  marketing: boolean;
}): Promise<void> {
  const payload = consent ?? readStoredCookieConsentForFlush();
  if (!payload) return;

  try {
    await apiClient.post(COOKIE_CONSENT_API_PATH, {
      analytics: payload.analytics,
      marketing: payload.marketing,
    });
  } catch (error) {
    console.warn('Failed to persist cookie consent; will retry on next auth sync', error);
  }
}
